import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function PlatformSettings() {
  const queryClient = useQueryClient();
  const [amberDays, setAmberDays] = useState<string>('90');
  const [redDays, setRedDays] = useState<string>('180');
  const [saving, setSaving] = useState(false);

  useQuery({
    queryKey: ['platform_stale_thresholds'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_platform_stale_thresholds');
      if (error) throw error;
      const d = data as { amber_days: number; red_days: number };
      setAmberDays(String(d.amber_days));
      setRedDays(String(d.red_days));
      return d;
    },
    staleTime: 5 * 60 * 1000,
  });

  const save = async () => {
    const amber = parseInt(amberDays, 10);
    const red   = parseInt(redDays, 10);
    if (isNaN(amber) || amber < 7 || amber > 365) {
      toast.error('Amber threshold must be a number between 7 and 365 days.');
      return;
    }
    if (isNaN(red) || red <= amber || red > 730) {
      toast.error('Red threshold must be a number greater than amber and no more than 730 days.');
      return;
    }
    setSaving(true);
    try {
      const { error: e1 } = await (supabase as any)
        .from('platform_settings')
        .update({ value: amber })
        .eq('key', 'stale_price_amber_days');
      if (e1) throw e1;

      const { error: e2 } = await (supabase as any)
        .from('platform_settings')
        .update({ value: red })
        .eq('key', 'stale_price_red_days');
      if (e2) throw e2;

      queryClient.invalidateQueries({ queryKey: ['platform_stale_thresholds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] });
      toast.success('Stale price thresholds updated.');
    } catch (err: any) {
      toast.error(err.message ?? 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-xl font-semibold text-white">Platform Settings</h1>
        <p className="text-sm text-slate-400 mt-1">
          Configure platform-wide defaults that apply to all companies.
        </p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Stale Price Thresholds</CardTitle>
          <CardDescription className="text-slate-400">
            Control when system line item prices are flagged as approaching stale (amber) or
            overdue for review (red). These thresholds apply to all companies on the platform.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-200">Amber warning (days)</label>
              <Input
                type="number"
                min={7}
                max={365}
                value={amberDays}
                onChange={(e) => setAmberDays(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-slate-500">
                Prices older than this many days show an amber warning. Minimum 7, maximum 365.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-200">Red warning (days)</label>
              <Input
                type="number"
                min={parseInt(amberDays, 10) + 1 || 8}
                max={730}
                value={redDays}
                onChange={(e) => setRedDays(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-slate-500">
                Prices older than this many days show a red warning. Must be greater than amber.
                Maximum 730.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-800">
            <p className="text-xs text-slate-400">
              Current: amber at {amberDays}d · red at {redDays}d
            </p>
            <Button onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Save Thresholds'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
